import { AppState } from '../stores/app-state'
import { logger } from '../utils/logger'

export class SettingsModal {
  private modal: HTMLElement | null = null
  private appState: AppState

  constructor(appState: AppState) {
    this.appState = appState
  }

  public show(): void {
    this.createModal()
    if (this.modal) {
      document.body.appendChild(this.modal)
      this.modal.classList.remove('hidden')
      this.attachEventListeners()
    }
  }

  public hide(): void {
    if (this.modal) {
      this.modal.classList.add('hidden')
      setTimeout(() => {
        if (this.modal && this.modal.parentNode) {
          this.modal.parentNode.removeChild(this.modal)
        }
        this.modal = null
      }, 200)
    }
  }

  private createModal(): void {
    // Remove existing modal if any
    this.hide()

    const isAdvancedMode = this.appState.getAdvancedMode()

    this.modal = document.createElement('div')
    this.modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 hidden'
    this.modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all duration-200 scale-95 opacity-0" id="settings-modal-content">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Settings</h2>
            <button 
              id="settings-close-btn"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="px-6 py-4 space-y-6">
          <!-- Workflow Mode Setting -->
          <div class="space-y-3">
            <h3 class="text-sm font-medium text-gray-900">Workflow Mode</h3>
            <p class="text-sm text-gray-600">
              Choose between Standard Mode (3 steps) for a streamlined experience or Classic Mode (4 steps) for more detailed control.
            </p>
            
            <div class="space-y-2">
              <label class="flex items-center space-x-3 cursor-pointer">
                <input 
                  type="radio" 
                  name="workflow-mode" 
                  value="standard" 
                  ${isAdvancedMode ? 'checked' : ''}
                  class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-900">Standard Mode</div>
                  <div class="text-xs text-gray-500">3 steps: Projects → Input & Capture → Plot & Edit</div>
                </div>
              </label>
              
              <label class="flex items-center space-x-3 cursor-pointer">
                <input 
                  type="radio" 
                  name="workflow-mode" 
                  value="classic" 
                  ${!isAdvancedMode ? 'checked' : ''}
                  class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-900">Classic Mode</div>
                  <div class="text-xs text-gray-500">4 steps: Projects → Input & Capture → Descriptions → Plot</div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button 
            id="settings-cancel-btn"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
          <button 
            id="settings-save-btn"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    `

    // Animate modal in
    setTimeout(() => {
      const content = this.modal?.querySelector('#settings-modal-content')
      if (content) {
        content.classList.remove('scale-95', 'opacity-0')
        content.classList.add('scale-100', 'opacity-100')
      }
    }, 10)
  }

  private attachEventListeners(): void {
    if (!this.modal) return

    // Close button
    const closeBtn = this.modal.querySelector('#settings-close-btn')
    closeBtn?.addEventListener('click', () => {
      this.hide()
    })

    // Cancel button
    const cancelBtn = this.modal.querySelector('#settings-cancel-btn')
    cancelBtn?.addEventListener('click', () => {
      this.hide()
    })

    // Save button
    const saveBtn = this.modal.querySelector('#settings-save-btn')
    saveBtn?.addEventListener('click', () => {
      this.saveSettings()
    })

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hide()
      }
    })

    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.hide()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)
  }

  private saveSettings(): void {
    if (!this.modal) return

    const selectedMode = this.modal.querySelector('input[name="workflow-mode"]:checked') as HTMLInputElement
    if (!selectedMode) return

    const newIsAdvancedMode = selectedMode.value === 'standard'
    const currentIsAdvancedMode = this.appState.getAdvancedMode()

    if (newIsAdvancedMode !== currentIsAdvancedMode) {
      // Update the app state
      this.appState.setAdvancedMode(newIsAdvancedMode)
      
      // Reset to first step when switching modes
      this.appState.setCurrentStep(1)
      
      // Trigger a re-render of the step content to reflect the mode change
      window.dispatchEvent(new CustomEvent('render-step-content'))
      
      logger.debug('Workflow mode changed:', newIsAdvancedMode ? 'Standard (3 steps)' : 'Classic (4 steps)')
    }

    this.hide()
  }
}

// Export a function to show the settings modal
export function showSettingsModal(appState: AppState): SettingsModal {
  const settings = new SettingsModal(appState)
  settings.show()
  return settings
}
